export interface ClusterSummaryInput {
  photoCount: number;
  startDate: Date;
  endDate: Date;
  location: string | null;
  participantNames: string[];
}

/** 1 -> "Sarah"; 2 -> "Sarah & Mom"; 3 -> "Sarah, Mom & Dad"; 4+ -> "Sarah, Mom, Dad +2 more" */
export function formatNameList(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]} & ${names[2]}`;
  return `${names[0]}, ${names[1]}, ${names[2]} +${names.length - 3} more`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateRange(start: Date, end: Date): string {
  if (isSameDay(start, end)) return formatDate(start);
  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    const month = start.toLocaleDateString('en-US', { month: 'short' });
    return `${month} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
  }
  return `${formatDate(start)} – ${formatDate(end)}`;
}

export function weekdayName(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

/** Buckets an EXIF capture hour into a casual time-of-day label — free signal, no new data needed. */
export function timeOfDay(d: Date): string {
  const h = d.getHours();
  if (h < 5) return 'Late Night';
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  if (h < 21) return 'Evening';
  return 'Night';
}

/**
 * Heuristic title — no LLM, built only from EXIF date/time, reverse-geocoded
 * location, and face-matched participant names.
 */
export function generateTitle(input: ClusterSummaryInput): string {
  const names = formatNameList(input.participantNames);
  if (input.location && names) return `${input.location} with ${names}`;
  if (input.location) return input.location;
  if (names) return `With ${names}`;
  // Neither location nor a matched face — lean on the one signal every photo
  // always has (its capture time) rather than falling back to a bare date,
  // which reads as a placeholder rather than a title.
  if (isSameDay(input.startDate, input.endDate)) {
    return `${weekdayName(input.startDate)} ${timeOfDay(input.startDate)}`;
  }
  return `Photos from ${formatDateRange(input.startDate, input.endDate)}`;
}

export function generateDescription(input: ClusterSummaryInput): string {
  const dateText = isSameDay(input.startDate, input.endDate)
    ? `on ${formatDate(input.startDate)}`
    : `from ${formatDateRange(input.startDate, input.endDate)}`;

  let text = `${input.photoCount} photo${input.photoCount === 1 ? '' : 's'} ${dateText}`;
  if (input.location) text += ` in ${input.location}`;
  text += '.';

  const names = formatNameList(input.participantNames);
  if (names) text += ` With ${names}.`;
  return text;
}
